import React, { useEffect, useState } from 'react'
import { Box, CircularProgress, Divider, useTheme } from '@mui/material'
import Search from '../../components/Search'
import { useQuery } from '@tanstack/react-query'
import { UserManager } from '../../APIManager'
import { UpdateEnrollments } from './components/UpdateEnrollments'
import type { IUser } from '../../models/User'
import type { Role } from '../../models/Role'
import type { IEnrollment } from '../../models/Enrollment'
import { StudentListSection } from './components/StudentListSection'
import { updateEnrollmentLists } from './helper'
import { ModalDefaultAlert } from '../../components/ModalDefaultAlert'

const EMPTY_USERS: IUser[] = []

interface StudentsAndEnrollmentProps {
  termId: string
  abbreviation: string
  sectionId: string
  newlyEnrolled?: IUser[]
  newlyUnenrolled?: IUser[]
  isConfirmModalOpen?: boolean
  setNewlyEnrolled?: React.Dispatch<React.SetStateAction<IUser[]>>
  setNewlyUnenrolled?: React.Dispatch<React.SetStateAction<IUser[]>>
  setIsConfirmModalOpen?: React.Dispatch<React.SetStateAction<boolean>>
  handleSubmit?: () => void
  readOnly?: true
  onAllClose?: () => void
  slotId?: string
  sectionStatus?: string
}

export const StudentsAndEnrollment: React.FC<StudentsAndEnrollmentProps> = ({
  termId,
  abbreviation,
  newlyEnrolled,
  newlyUnenrolled,
  setNewlyEnrolled,
  setNewlyUnenrolled,
  isConfirmModalOpen,
  setIsConfirmModalOpen,
  handleSubmit,
  sectionId,
  readOnly,
  onAllClose,
  slotId = '',
  sectionStatus
}) => {
  const hasNewlyEnrolledStudents = Array.isArray(newlyEnrolled) && newlyEnrolled.length > 0
  const theme = useTheme()
  const [search, setSearch] = useState('')
  const [unenrolled, setUnenrolled] = useState<IUser[]>([])
  const [enrolled, setEnrolled] = useState<IUser[]>([])
  const { data: studentRoleId, isLoading: isRoleLoading } = useQuery({
    queryKey: ['studentRoleId'],
    queryFn: async () => {
      const listOfRoleIds = (await UserManager.getRoleApi({ isStudent: true })).data
      return listOfRoleIds.find((role: Role) => role?.roleName === 'Student')?.roleId || ''
    },
    enabled: !readOnly
  })

  const { data: enrollmentResults = EMPTY_USERS, isLoading: isEnrolledResultsLoading } = useQuery({
    queryKey: ['enrollment', termId, abbreviation, sectionId],
    queryFn: async () => {
      const response = await UserManager.getInstructorsBySectionIdApi(sectionId)
      return (
        (Array.isArray(response?.data) ? response.data : [])
          .map((student: IEnrollment) => student.user as IUser)
      )
    },
    enabled: !!termId && !!abbreviation && !!sectionId
  })

  // sets enrolled list based on the results from the API
  useEffect(() => { enrollmentResults && setEnrolled(enrollmentResults) }, [enrollmentResults])

  // fetches all students based on search
  const { data: searchResults, isLoading: isSearchLoading } = useQuery({
    queryKey: ['search', search],
    queryFn: async () => {
      return (await UserManager.getUsersSummaryApi({ search })).data || []
    },
    enabled: search.trim() !== ''
  })

  // updates enrolled and unenrolled based on search results
  useEffect(() => {
    updateEnrollmentLists(
      search,
      enrollmentResults,
      newlyEnrolled,
      newlyUnenrolled,
      searchResults,
      setEnrolled,
      setUnenrolled
    )
  }, [search, searchResults, enrollmentResults, newlyEnrolled, newlyUnenrolled])

  const handleRemoveEnrollment = async (userId: string): Promise<void> => {
    const enrollmentOfStudent = enrolled.find((student: IUser) => student?.userId === userId)
    const enrollmentInEnrolledResults = enrollmentResults?.find((student: IUser) => student?.userId === userId)
    const enrollmentInUnenrolled = unenrolled.find((student: IUser) => student?.userId === userId)
    const enrollmentInNewlyUnenrolled = newlyUnenrolled?.find((student: IUser) => student?.userId === userId) ?? null
    // add student to unenrolled list
    setUnenrolled((prev) => enrollmentInUnenrolled ? [...prev] : [...prev, enrollmentOfStudent as IUser])
    // remove from newly enrolled list) {
    setNewlyEnrolled?.((prev) => prev.filter((enrollment) => enrollment?.userId !== userId))
    // add student to newly unenrolled list
    setNewlyUnenrolled?.((prev) => !enrollmentInEnrolledResults || enrollmentInNewlyUnenrolled ? [...prev] : [...prev, enrollmentOfStudent as IUser])
    // remove student from enrolled list
    setEnrolled?.((prev) => prev.filter((student) => student?.userId !== userId))
  }

  const handleAddEnrollment = async (userId: string): Promise<void> => {
    const enrollmentOfStudent = unenrolled.find((student: IUser) => student?.userId === userId)
    const enrollmentInEnrolledResults = enrollmentResults?.find((student: IUser) => student?.userId === userId)
    const enrollmentInEnrolled = enrolled.find((student: IUser) => student?.userId === userId)
    const enrollmentInNewlyEnrolled = newlyEnrolled?.find((student: IUser) => student?.userId === userId) ?? null
    // add student to enrolled list
    setEnrolled((prev) => enrollmentInEnrolled ? [...prev] : [...prev, enrollmentOfStudent as IUser])
    // if found, remove from newly unenrolled list
    setNewlyUnenrolled?.((prev) => prev.filter((enrollment) => enrollment?.userId !== userId))
    // if student is not in enrolledresults or already in newlyEnrolled, add to newly enrolled list
    setNewlyEnrolled?.((prev) => (enrollmentInEnrolledResults ?? enrollmentInNewlyEnrolled) ? [...prev] : [...prev, enrollmentOfStudent as IUser])
    // if student is in unenrolled, remove from unenrolled list
    setUnenrolled((prev) => prev.filter((student) => student?.userId !== userId))
  }

  const handleSearch = (value: string): void => {
    setSearch(value)
  }

  const isSomethingLoading = isSearchLoading || isEnrolledResultsLoading || isRoleLoading
  const displayRequirements = search.trim() !== '' && !isSearchLoading && !readOnly

  return (
    <>
      <Box sx={{ my: 1 }}>
        <Search placeholder='Search by first name, last name, or email' onSearch={handleSearch} fillContainer={true} />
      </Box>
      {isSomethingLoading
        ? <Box display='flex' justifyContent='center' alignItems='center' minHeight={theme.spacing(20)}>
          <CircularProgress thickness={4} value={2}/>
        </Box>
        : <>
          <StudentListSection
            title='Enrolled in Course Section'
            students={enrolled}
            onAction={userId => { void handleRemoveEnrollment(userId) }}
            readOnly={readOnly}
            enrolled
            sectionId={sectionId}
            showAllInstructorForNewlyAdded={hasNewlyEnrolledStudents}
            newlyEnrolled={newlyEnrolled}
            slotId={slotId}
            sectionStatus={sectionStatus}
          />
          {enrollmentResults?.length === 0 && enrolled?.length === 0 &&
              <ModalDefaultAlert message="No students are currently enrolled in this course section." />
          }
          {displayRequirements &&
            <>
              <Divider sx={{ my: 4 }} />
              <StudentListSection
                title='Not Enrolled in Course Section'
                students={unenrolled}
                onAction={userId => { void handleAddEnrollment(userId) }}
                notEnrolledSection={true}
                slotId={slotId}
              />
            </>
          }
        </>

      }
      {!readOnly && (
        <UpdateEnrollments
          newlyEnrolled={newlyEnrolled ?? []}
          newlyUnenrolled={newlyUnenrolled ?? []}
          isConfirmModalOpen={isConfirmModalOpen ?? false}
          setIsConfirmModalOpen={setIsConfirmModalOpen ?? (() => { })}
          handleSubmit={handleSubmit ?? (() => { })}
          sectionId={sectionId ?? ''}
          termId={termId ?? ''}
          studentRoleId={studentRoleId ?? ''}
          onAllClose={onAllClose}
        />
      )}
    </>
  )
}
